"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ReactNode } from "react";
import {
	FieldError,
	Label,
	TextArea as RACTextArea,
	type TextAreaProps as RACTextAreaProps,
	Text,
	TextField,
	type TextFieldProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useTranslations } from "@/i18n/client";

const textareaStyles = cva(
	[
		"block w-full bg-transparent outline-none resize-y",
		"font-body font-regular text-base leading-[1.15]",
		"placeholder:text-text-dark-text-2",
		"data-[disabled]:cursor-not-allowed",
		"border border-solid",
		"rounded-radius-component-specific-input",
		"transition-colors duration-200",
		"bg-input-default-bg",
		"data-[disabled]:bg-input-disabled-bg",
	],
	{
		variants: {
			size: {
				small: "h-24 pl-xs pr-3-xs py-2-xs",
				medium: "h-30 pl-xs pr-3-xs py-2-xs",
				large: "h-36 pl-xs pr-2-xs py-2-xs",
			},
			tone: {
				default: [
					"text-text-dark-text-1",
					"border-input-default-border",
					"data-[hovered]:border-input-hover-border",
					"data-[focused]:border-input-active-border",
					"data-[disabled]:border-input-disabled-border",
					"data-[disabled]:text-text-dark-text-2",
				],
				error: [
					"text-status-error-text",
					"placeholder:text-status-error-text",
					"border-input-error-border",
				],
				success: ["text-status-success-text", "border-input-success-border"],
			},
		},
		defaultVariants: {
			size: "medium",
			tone: "default",
		},
	},
);

const messageStyles = cva("font-body text-xs leading-[1.5]", {
	variants: {
		tone: {
			default: "text-text-dark-text-1",
			error: "text-status-error-text",
			success: "text-status-success-text",
		},
	},
	defaultVariants: { tone: "default" },
});

type TextareaVariants = VariantProps<typeof textareaStyles>;

export type TextareaProps = Omit<TextFieldProps, "children"> &
	Pick<RACTextAreaProps, "placeholder" | "rows" | "cols"> & {
		size?: NonNullable<TextareaVariants["size"]>;
		label?: ReactNode;
		isOptional?: boolean;
		description?: ReactNode;
		errorMessage?: ReactNode;
		successMessage?: ReactNode;
		isSuccess?: boolean;
		className?: string;
		textareaClassName?: string;
	};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	(
		{
			size = "medium",
			label,
			isOptional = false,
			description,
			errorMessage,
			successMessage,
			isInvalid,
			isSuccess = false,
			placeholder,
			rows,
			cols,
			className,
			textareaClassName,
			...textFieldProps
		},
		ref,
	) => {
		const t = useTranslations("form");
		const tone: "default" | "error" | "success" = isInvalid
			? "error"
			: isSuccess
				? "success"
				: "default";

		return (
			<TextField
				{...textFieldProps}
				isInvalid={isInvalid}
				className={twMerge("flex flex-col gap-2-xs w-full", className)}
			>
				{label != null && (
					<div className="flex gap-3-xs items-start w-full">
						<Label className="flex-1 font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text-1 tabular-nums lining-nums">
							{label}
						</Label>
						{isOptional && (
							<span className="font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text-2 tabular-nums lining-nums">
								{t("optional")}
							</span>
						)}
					</div>
				)}

				<RACTextArea
					ref={ref}
					placeholder={placeholder}
					rows={rows}
					cols={cols}
					className={twMerge(textareaStyles({ size, tone }), textareaClassName)}
				/>

				{tone === "error" && errorMessage != null ? (
					<FieldError className={messageStyles({ tone: "error" })}>{errorMessage}</FieldError>
				) : tone === "success" && successMessage != null ? (
					<Text slot="description" className={messageStyles({ tone: "success" })}>
						{successMessage}
					</Text>
				) : description != null ? (
					<Text slot="description" className={messageStyles({ tone: "default" })}>
						{description}
					</Text>
				) : null}
			</TextField>
		);
	},
);

Textarea.displayName = "Textarea";
